<template>
    <el-dialog :title="finalDialogTitle" :visible.sync="localDialogVisible" :width="dialogWidth"
        class="custom-form-dialog dialog-with-radius" :close-on-click-modal="closeOnClickModal"
        :modal="showModal" :append-to-body="appendToBody" :modal-append-to-body="modalAppendToBody"
        @close="handleDialogClose">
        <!-- 弹窗主体内容 -->
        <div class="detail-dialog-body" :style="bodyStyle">
            <!-- 👇 动态自定义模块区域（放在详情字段前面） -->
            <div v-for="(section, index) in customSections" :key="index" class="custom-section-wrapper">
                <slot :name="section.slotName" :detailData="detailData" :sectionConfig="section"></slot>
            </div>

            <!-- 方式1：通过插槽完全自定义内容 -->
            <slot name="detail-content">
                <!-- 方式2：使用内置 DetailSection 渲染结构化数据 -->
                <DetailSection v-if="sections && sections.length" :sections="sections" :buttons="[]" />
                <!-- 如果既无插槽也无 sections，则显示空提示 -->
                <div v-else ></div>
            </slot>
        </div>

        <!-- 底部按钮组（可选） -->
        <template #footer v-if="showFooter">
            <el-button v-for="(btn, idx) in footerButtons" :key="idx" :size="buttonSize" :type="btn.type || 'primary'"
                :plain="btn.plain" :disabled="btn.disabled" @click="handleButtonClick(btn, idx)"
                :class="['dialog-footer-btn', btn.class]">
                <i v-if="btn.icon" :class="btn.icon"></i>
                <span>{{ btn.text }}</span>
            </el-button>
        </template>
    </el-dialog>
</template>

<script>
import DetailSection from './DetailSection.vue'; // 请根据实际路径调整

export default {
    name: 'DetailDialog',
    components: {
        DetailSection
    },
    props: {
        // 弹窗标题
        dialogTitle: {
            type: String,
            default: ''
        },
        // 弹窗类型（用于生成默认标题）
        dialogType: {
            type: String,
            validator: val => ['view', 'detail', 'info'].includes(val),
            default: 'detail'
        },
        // 显隐控制（.sync）
        dialogVisible: {
            type: Boolean,
            required: true
        },
        // 弹窗宽度
        dialogWidth: {
            type: [String, Number],
            default: '600px'
        },
        // 是否点击遮罩关闭
        closeOnClickModal: {
            type: Boolean,
            default: false
        },
        // 是否显示底部按钮
        showFooter: {
            type: Boolean,
            default: true
        },
        // 底部按钮配置
        footerButtons: {
            type: Array,
            default: () => [
                { text: '关闭', type: 'primary' }
            ]
        },
        // 按钮尺寸
        buttonSize: {
            type: String,
            default: 'medium',
            validator: val => ['large', 'medium', 'small', 'mini'].includes(val)
        },
        // 用于 DetailSection 的 sections 数据（可选）
        sections: {
            type: Array,
            default: () => []
        },
        customSections: {
            type: Array,
            default: () => []
        },
        detailData: {
            type: Object,
            default: () => ({})
        },
        // 自定义内容样式（用于 iframe 等场景，不限制高度）
        customBodyStyle: {
            type: Object,
            default: () => ({})
        },
        // 是否显示遮罩层
        modal: {
            type: Boolean,
            default: true
        },
        // 遮罩层是否插入到 body
        modalAppendToBody: {
            type: Boolean,
            default: true
        },
        // dialog 是否插入到 body
        appendToBody: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            localDialogVisible: this.dialogVisible
        };
    },
    watch: {
        dialogVisible(newVal) {
            this.localDialogVisible = newVal;
        }
    },
    computed: {
        finalDialogTitle() {
            return this.dialogTitle || (this.dialogType === 'view' ? '查看详情' : '信息详情');
        },
        showModal() {
            return this.modal;
        },
        bodyStyle() {
            // 如果有自定义样式，则使用自定义样式
            if (this.customBodyStyle && Object.keys(this.customBodyStyle).length > 0) {
                return this.customBodyStyle;
            }
            // 默认样式
            return {
                maxHeight: '600px',
                overflowY: 'auto',
                padding: '0 20px'
            };
        }
    },
    methods: {
        handleButtonClick(btn, index) {
            if (btn.onClick && typeof btn.onClick === 'function') {
                btn.onClick(btn, index);
            } else {
                // 默认行为：如果是“关闭”类按钮，自动关闭弹窗
                const closeKeywords = ['关闭', 'cancel', 'close'];
                if (closeKeywords.some(kw => btn.text.includes(kw))) {
                    this.closeDialog();
                }
            }
        },

        closeDialog() {
            this.localDialogVisible = false;
            this.$emit('update:dialogVisible', false);
            this.$emit('close');
        },

        handleDialogClose() {
            this.closeDialog();
        }
    }
};
</script>

<style scoped>
/* 复用原弹窗样式 */
::v-deep .el-dialog__title {
    font-weight: 800 !important;
}

::v-deep .el-dialog__header {
    background-color: #EFF5FF !important;
}

::v-deep .el-dialog {
    border-radius: 20px !important;
    overflow: hidden !important;
}

::v-deep .el-dialog__header {
    padding: 16px 20px;
    border-bottom: 1px solid #f5f5f5;
}

::v-deep .el-dialog__body {
    padding: 0 !important;
}

::v-deep .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #f5f5f5;
    text-align: center;
    background-color: #F9FAFB;
}

.dialog-footer-btn {
    min-width: 100px;
    margin-left: 12px;
    border-radius: 10px !important;
    font-weight: 600 !important;
    padding: 10px 20px !important;
    transition: all 0.3s !important;
}

.dialog-footer-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* 美化 success 按钮 */
.dialog-footer-btn.el-button--success {
    background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%) !important;
    border-color: #67c23a !important;
}

/* 美化 primary 按钮 */
.dialog-footer-btn.el-button--primary {
    background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%) !important;
    border-color: #8b5cf6 !important;
}

/* 美化 plain 按钮 */
.dialog-footer-btn.is-plain {
    background: #ffffff !important;
    border-color: #dcdfe6 !important;
    color: #606266 !important;
}

.dialog-footer-btn.is-plain:hover {
    border-color: #8b5cf6 !important;
    color: #8b5cf6 !important;
    background: #f5f3ff !important;
}

.empty-tip {
    text-align: center;
    color: #999;
    padding: 40px 0;
}
</style>